<script setup lang="ts">
import Menu from './Menu.vue';
import AnnounceBar from './AnnounceBar.vue';
import { UserControl } from '~/entities/user-control/';
import { Search } from '~/entities/search';
import { useOrigin } from '~/shared/lib/useOrigin';
import { HEADER_MENU_ITEMS } from '~/shared/lib/menu-links.const';
import { ANNOUNCE_BAR } from '~/shared/lib/announce-bar.const';
import { useAnnounceBar } from '~/shared/lib/useCountdown';

const { $viewport } = useNuxtApp();

const {
  isVisible: isAnnounceVisible,
  days: announceDays,
  hours: announceHours,
} = useAnnounceBar();

const menuItems = computed(() => {
  if (!isAnnounceVisible.value || !ANNOUNCE_BAR.hiddenMenuLink) {
    return HEADER_MENU_ITEMS;
  }

  return HEADER_MENU_ITEMS.filter(
    (item) => item.link !== ANNOUNCE_BAR.hiddenMenuLink,
  );
});

const isOverlayVisible = ref(false);

const syncBodyScroll = () => {
  if (!import.meta.client) return;

  const shouldLockBodyScroll =
    isOverlayVisible.value && $viewport.match('desktop');

  document.body.style.overflow = shouldLockBodyScroll ? 'hidden' : '';
};

const toggleOverlay = () => {
  isOverlayVisible.value = !isOverlayVisible.value;
  syncBodyScroll();
};

watch($viewport.breakpoint, () => {
  syncBodyScroll();
});

const sentinelRef = ref<HTMLElement | null>(null);
const headerRef = ref<HTMLElement | null>(null);
const isFixedHeader = ref(false);

const isHeaderStuck = computed(
  () => $viewport.isGreaterOrEquals('tabletBig') && isFixedHeader.value,
);

const hostLink = computed(() => {
  return useOrigin();
});

onMounted(() => {
  if (!sentinelRef.value) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(
      (entry) => {
        isFixedHeader.value = !entry.isIntersecting;
      },
      {
        threshold: 0,
        rootMargin: '0px 0px 0px 0px',
      },
    );
  });

  observer.observe(sentinelRef.value);

  onBeforeUnmount(() => {
    observer.disconnect();
  });
});

onBeforeUnmount(() => {
  if (!import.meta.client) return;

  document.body.style.overflow = '';
});
</script>

<template>
  <div
    :class="{
      [$style.root_fixed!]: isHeaderStuck,
      [$style.root_fixedTwoRows!]: isHeaderStuck && isAnnounceVisible,
    }"
  >
    <div ref="sentinelRef" :class="$style.sentinel" />

    <div
      ref="headerRef"
      :class="[
        $style.header,
        {
          [$style.header_fixed!]: isHeaderStuck,
        },
      ]"
    >
      <div
        :class="[
          $style.header__inner,
          { [$style.header__inner_compact!]: isAnnounceVisible },
        ]"
      >
        <Menu
          :is-opened="isOverlayVisible"
          :is-header-two-rows="isAnnounceVisible"
          @toggle="toggleOverlay"
        />
        <NuxtLink
          :to="hostLink"
          :class="[
            $style.header__logo,
            { [$style.header__logo_compact!]: isAnnounceVisible },
          ]"
        />

        <div v-if="isAnnounceVisible" :class="$style.header__announce">
          <AnnounceBar :days="announceDays" :hours="announceHours" />
        </div>

        <div v-else :class="$style.header__nav">
          <NuxtLink
            v-for="(item, key) in menuItems"
            :key="`headerMenu_${key}`"
            :to="item.link"
            :target="item.newTab ? '_blank' : undefined"
            :title="item.title"
            :class="[
              $style.header__menuItem,
              { [$style.header__menuItem_wrap!]: item.wrap },
            ]"
          >
            <Icon
              v-if="item.logo"
              :name="`my-icon:${item.logo}`"
              :class="$style.header__menuItemLogo"
            />
            <template v-else>{{ item.title }}</template>
            <Icon
              v-if="item.badge"
              :name="`my-icon:i-badge-${item.badge}`"
              :width="47"
              :height="20"
              :class="$style.header__menuItemIcon"
            />
          </NuxtLink>
        </div>
        <Search
          :expanded="isAnnounceVisible"
          :class="[
            $style.header__search,
            { [$style.header__search_expanded!]: isAnnounceVisible },
          ]"
        />
        <UserControl />
      </div>

      <div
        v-if="isAnnounceVisible"
        :class="[$style.header__inner, $style.header__inner_second]"
      >
        <div :class="[$style.header__nav, $style.header__nav_second]">
          <NuxtLink
            v-for="(item, key) in menuItems"
            :key="`headerMenuSecond_${key}`"
            :to="item.link"
            :target="item.newTab ? '_blank' : undefined"
            :title="item.title"
            :class="$style.header__menuItem"
          >
            <Icon
              v-if="item.logo"
              :name="`my-icon:${item.logo}`"
              :class="$style.header__menuItemLogo"
            />
            <template v-else>{{ item.title }}</template>
            <Icon
              v-if="item.badge"
              :name="`my-icon:i-badge-${item.badge}`"
              :width="47"
              :height="20"
              :class="$style.header__menuItemIcon"
            />
          </NuxtLink>
        </div>
      </div>
      <div
        v-if="isOverlayVisible"
        :class="$style.header__overlay"
        @click="toggleOverlay"
      ></div>
    </div>
  </div>
</template>

<style module lang="scss">
.root {
  &_fixed {
    padding-bottom: 64px;
  }

  /* 54px основной ряд + 42px второй этаж */
  &_fixedTwoRows {
    padding-bottom: 96px;
  }
}

.sentinel {
  height: 0px;
}

.header {
  position: relative;

  &_fixed {
    position: fixed;
    top: 0;
    width: inherit;
    z-index: 5;
    width: 1380px;
  }

  &__inner {
    position: relative;
    z-index: 10;
    display: flex;
    flex-flow: row nowrap;
    width: 100%;
    min-width: 0;
    height: 64px;
    background-color: var(--color-white);
    border-bottom: 1px solid var(--color-gray-line);

    &_compact {
      height: 54px;
    }

    &_second {
      height: 42px;
    }
  }

  &__overlay {
    position: fixed;
    z-index: 5;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #130002;
    opacity: 0.7;
    cursor: pointer;
  }

  &__logo {
    display: block;
    flex-shrink: 0;
    border-right: 1px solid var(--color-gray-line);
    border-left: 1px solid var(--color-gray-line);
    width: 237px;
    height: 100%;
    background: url('images/logo.svg') no-repeat left 20px center;
    cursor: pointer;

    &_compact {
      background-size: 170px 40px;
      background-position: center;
    }
  }

  &__announce {
    display: flex;
    flex: 1 1 auto;
    align-items: stretch;
    min-width: 0;
    border-right: 1px solid var(--color-gray-line);
  }

  &__nav {
    display: flex;
    flex: 1 1 auto;
    justify-content: space-around;
    min-width: 0;
    border-right: 1px solid var(--color-gray-line);

    &_second {
      border-right: none;
      padding: 0 var(--space-2x);
    }
  }

  &__menuItem {
    position: relative;
    display: flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
    letter-spacing: -0.55px;
    /* Пункты не переносим и не сжимаем, иначе текст ломается на две строки */
    white-space: nowrap;
    flex-shrink: 0;
    @include text_body2_medium;
    color: var(--color-black-1);
    padding-left: var(--space-1x);
    padding-right: var(--space-1x);

    &:hover,
    &_active,
    &:global(.router-link-active) {
      color: var(--color-red-1);
      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -1px;
        width: 100%;
        height: 3px;
        background-color: var(--color-red-1);
      }
    }

    &Icon {
      position: relative;
      top: -1px;
    }

    &Logo {
      display: block;
      width: auto;
      height: 16px;
    }
  }

  &__search {
    flex: 0 0 var(--space-8x);
    border-right: 1px solid var(--color-gray-line);

    &_expanded {
      flex-basis: 361px;
      border-left: 1px solid var(--color-gray-line);
    }
  }
}

@media (max-width: 1379px) {
  .header {
    &__nav {
      flex-grow: 1;
      justify-content: space-around;
    }

    &__search {
      flex-grow: unset;

      &_expanded {
        flex-basis: 220px;
      }
    }
  }
}

@media (min-width: 1024px) and (max-width: 1379px) {
  .header {
    &_fixed {
      width: 1024px;
    }

    &__logo {
      width: 170px;
      background-size: 140px auto;
      background-position: left var(--space-2x) center;
    }

    &__menuItem {
      padding-left: 0;
      padding-right: 0;
      font-size: 12px;
      white-space: nowrap;

      &_wrap {
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0;
        white-space: normal;
        text-align: center;
        line-height: 1.15;
        flex-shrink: 1;
        max-width: min-content;
      }

      &Logo {
        height: 13px;
      }
    }
  }
}

@media (max-width: 1023px) {
  .header {
    /* Ниже 1024 пункты меню заменяет всплывающее меню под бургером */
    &__nav {
      display: none;
    }

    &__inner_second {
      display: none;
    }

    &__announce {
      order: 1;
      flex-basis: 100%;
      height: 48px;
      border-right: none;
      /* Закрываем линию ряда: над плашкой границы быть не должно */
      margin-bottom: -1px;
    }

    &__inner {
      flex-wrap: wrap;
      align-content: flex-start;

      &_compact {
        height: 100%;
        > *:not([class*='announce']) {
          height: var(--space-8x);
        }
      }
    }

    &__search {
      flex-grow: 1;

      &_expanded {
        flex-basis: var(--space-8x);
        flex-grow: 1;
      }
    }
  }
}

@media (max-width: 767px) {
  .header {
    &__logo {
      /* Без min-width логотип держал 237px и выдавливал кнопки на третью строку */
      flex: 1 1 0;
      min-width: 0;
      height: var(--space-8x);
    }

    &__search {
      flex-grow: unset;
    }
  }
}

@media (max-width: 479px) {
  .header {
    &__logo {
      background-size: auto 40px;
      background-position: left var(--space-2x) center;
    }
  }
}
@media (max-width: 400px) {
  .header {
    &__logo {
      background-size: auto 33px;
    }
  }
}
</style>
